import { Spinner } from "@/components/ui/spinner";
import { cn, generateTextShadow } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { PrizeSparkles } from "@/components/PrizeSparkles";
import { PrizeDisplay } from "./PrizeDisplay";
import { MODEL_REGISTRY } from "@/components/RocketsOverlay";
import { getRewardIcon } from "./missionConstants";
import { RadialStarBurst } from "./RadialStarBurst";
import type { GachaPrizeItem } from "@/lib/types/api";

export function DailyBonusCard({
  missionsCompleted,
  missionsTotal,
  isComplete,
  isClaimed,
  isPending,
  onClaim,
  variant = "daily",
  prize,
  reward,
}: {
  missionsCompleted: number;
  missionsTotal: number;
  isComplete: boolean;
  isClaimed: boolean;
  isPending: boolean;
  onClaim: () => void;
  variant?: "daily" | "weekly";
  prize: GachaPrizeItem | null;
  reward: { type: string; amount: number };
}) {
  const progress = missionsTotal > 0 ? missionsCompleted / missionsTotal : 0;
  const isDaily = variant === "daily";
  const canClaim = isComplete && !isClaimed;

  return (
    <div
      className="mx-4 rounded-sm p-3 flex flex-col justify-center items-end gap-3"
      style={{
        aspectRatio: "32/9",
        backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(/assets/${isDaily ? "daily-progress-mission" : "weekly-progress-mission"}.png)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        imageRendering: "pixelated",
      }}
    >
      <style>{`
        @keyframes prize-pop-shake {
          0%, 100% { transform: scale(1) rotate(0deg); }
          15% { transform: scale(1.18) rotate(-5deg); }
          30% { transform: scale(1.12) rotate(5deg); }
          45% { transform: scale(1.15) rotate(-3deg); }
          60% { transform: scale(1.1) rotate(2deg); }
          75% { transform: scale(1.05) rotate(-1deg); }
        }
      `}</style>
      <div className="w-[65%]">
        {/* Content: progress bar left, prize right */}
        <div
          className={cn(
            "flex items-center justify-between p-2",
            isDaily
              ? "alternative-green-container"
              : "alternative-purple-container",
          )}
        >
          {/* Progress bar + count */}
          <div className="flex-1 flex flex-col gap-3 min-w-0 mr-3">
            {/* Title */}
            <h3
              className="text-white font-bold text-xs sm:text-sm uppercase"
              style={{ textShadow: generateTextShadow(1) }}
            >
              {t(isDaily ? "streakMissions.dailyMissionClear" : "streakMissions.weeklyMissionClear")}
            </h3>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 h-3 bg-gray-700/60 rounded-full overflow-hidden border border-gray-600/40"
                role="progressbar"
                aria-valuenow={missionsCompleted}
                aria-valuemin={0}
                aria-valuemax={missionsTotal}
                aria-label={`${missionsCompleted} of ${missionsTotal} missions completed`}
              >
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    isComplete
                      ? "bg-gradient-to-r from-green-400 to-green-500"
                      : "bg-gradient-to-r from-amber-400 to-yellow-500",
                  )}
                  style={{
                    width: `${progress * 100}%`,
                  }}
                />
              </div>
              <span
                className="text-white/80 text-xs font-bold shrink-0"
                aria-live="polite"
              >
                {missionsCompleted}/{missionsTotal}
              </span>
            </div>
          </div>

          {/* Tappable prize display */}
          <button
            type="button"
            disabled={!canClaim || isPending}
            onClick={canClaim && !isPending ? onClaim : undefined}
            className={cn(
              "relative flex flex-col items-center justify-center shrink-0 border-0 bg-transparent p-0",
              canClaim && !isPending && "cursor-pointer",
              (!canClaim || isPending) && "cursor-default",
            )}
            aria-label={
              isClaimed
                ? `${isDaily ? "Daily" : "Weekly"} bonus claimed`
                : canClaim
                  ? `Tap to claim ${isDaily ? "daily" : "weekly"} bonus`
                  : `Complete ${missionsTotal - missionsCompleted} more missions to claim`
            }
          >
            <RadialStarBurst className="-mt-1" />
            {prize ? (
              <>
                <PrizeDisplay
                  prize={prize}
                  className={cn(
                    "h-14",
                    isClaimed && "saturate-[.95] brightness-95",
                  )}
                />
                <span
                  className={cn(
                    "-mt-2 sm:-mt-4 text-xl sm:text-2xl text-white text-shadow-black font-bold uppercase leading-tight text-center",
                    prize.id in MODEL_REGISTRY && "-mt-3",
                  )}
                  //style={{ textShadow: generateTextShadow(1) }}
                >
                  {prize.name}
                </span>
              </>
            ) : (
              <>
                <img
                  src={getRewardIcon(reward.type)}
                  className={cn("h-5 sm:h-6 w-auto", isClaimed && "grayscale")}
                  style={{ imageRendering: "pixelated" }}
                  alt={reward.type}
                />
                <span
                  className={cn(
                    "-mt-2 sm:-mt-4 text-lg sm:text-xl text-white font-bold uppercase leading-tight text-center",
                    isClaimed && "grayscale opacity-60",
                  )}
                  style={{ textShadow: generateTextShadow(1) }}
                >
                  +{reward.amount}
                </span>

                {/* Claim tag */}
                {canClaim && !isPending && (
                  <span
                    className={cn(
                      "absolute -bottom-3 sm:-bottom-4 left-1/2 -translate-x-1/2 text-[12px] -tracking-tight uppercase whitespace-nowrap",
                      "group-hover:brightness-95 group-hover:saturate-95 group-active:scale-98 px-2 py-0.5 rounded-full z-10",
                      "text-[#7dfe03]",
                    )}
                    style={{
                      animation: "prize-pop-shake 0.85s ease-in-out infinite",
                      textShadow: generateTextShadow(0.35),
                    }}
                  >
                    {t("streakMissions.claim")}
                  </span>
                )}

                {/* Claimed label */}
                {isClaimed && (
                  <div className="flex flex-col items-center justify-center gap-px absolute -bottom-2 left-1/2 -rotate-8 -translate-x-1/2 font-extrabold uppercase whitespace-nowrap z-10">
                    <svg
                      className="w-7 h-auto"
                      viewBox="0 0 11 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.647059 2V2.66667H1.94118H2.58824V2H0.647059Z"
                        fill="black"
                      />
                      <path
                        d="M0.647059 2.66667H0V4.66667H0.647059V2.66667Z"
                        fill="black"
                      />
                      <path
                        d="M0.647059 4.66667V5.33333H1.29412V4.66667H0.647059Z"
                        fill="black"
                      />
                      <path
                        d="M1.29412 5.33333V6H1.94118V5.33333H1.29412Z"
                        fill="black"
                      />
                      <path
                        d="M1.94118 6V6.66667H2.58824V6H1.94118Z"
                        fill="black"
                      />
                      <path
                        d="M2.58824 6.66667V7.33333H3.23529V6.66667H2.58824Z"
                        fill="black"
                      />
                      <path
                        d="M3.23529 7.33333V8H5.82353V7.33333H3.23529Z"
                        fill="black"
                      />
                      <path
                        d="M5.82353 7.33333H6.47059V6.66667H5.82353V7.33333Z"
                        fill="black"
                      />
                      <path
                        d="M6.47059 6.66667H7.11765V6H6.47059V6.66667Z"
                        fill="black"
                      />
                      <path
                        d="M7.11765 6H7.76471V5.33333H7.11765V6Z"
                        fill="black"
                      />
                      <path
                        d="M7.76471 5.33333H8.41177V4.66667H7.76471V5.33333Z"
                        fill="black"
                      />
                      <path
                        d="M8.41177 4.66667H9.05882V4H8.41177V4.66667Z"
                        fill="black"
                      />
                      <path
                        d="M9.05882 4H9.70588V3.33333H9.05882V4Z"
                        fill="black"
                      />
                      <path
                        d="M9.05882 0.666667H8.41177V1.33333H9.05882V0.666667Z"
                        fill="black"
                      />
                      <path
                        d="M8.41177 1.33333H7.76471V2H8.41177V1.33333Z"
                        fill="black"
                      />
                      <path
                        d="M7.76471 2H7.11765V2.66667H7.76471V2Z"
                        fill="black"
                      />
                      <path
                        d="M7.11765 2.66667H6.47059V3.33333H7.11765V2.66667Z"
                        fill="black"
                      />
                      <path
                        d="M6.47059 3.33333H5.82353V4H6.47059V3.33333Z"
                        fill="black"
                      />
                      <path
                        d="M3.88235 3.33333H3.23529V4H3.88235V3.33333Z"
                        fill="black"
                      />
                      <path
                        d="M3.23529 2.66667H2.58824V3.33333H3.23529V2.66667Z"
                        fill="black"
                      />
                      <path
                        d="M3.88235 4V4.66667H4.52941V4H3.88235Z"
                        fill="black"
                      />
                      <path
                        d="M3.88235 4.66667V4H3.23529V4.66667H3.88235Z"
                        fill="white"
                      />
                      <path
                        d="M3.23529 4V3.33333H2.58824V4H3.23529Z"
                        fill="white"
                      />
                      <path
                        d="M2.58824 3.33333V2.66667H1.94118V3.33333H2.58824Z"
                        fill="white"
                      />
                      <path
                        d="M4.52941 4.66667H3.88235V5.33333H4.52941V4.66667Z"
                        fill="white"
                      />
                      <path
                        d="M5.17647 4.66667H5.82353V4H5.17647V4.66667Z"
                        fill="black"
                      />
                      <path
                        d="M4.52941 5.33333H5.17647V4.66667H4.52941V5.33333Z"
                        fill="black"
                      />
                      <path
                        d="M9.70588 3.33333H10.3529V2.66667H9.70588V3.33333Z"
                        fill="black"
                      />
                      <path
                        d="M10.3529 2.66667H11V0.666667H10.3529V2.66667Z"
                        fill="black"
                      />
                      <path
                        d="M9.05882 0V0.666667H10.3529V0H9.05882Z"
                        fill="black"
                      />
                      <path
                        d="M0.647059 2.66667V4.66667H1.29412V5.33333H1.94118V6H2.58824V6.66667H3.23529V7.33333H5.82353V6.66667H6.47059V6H7.11765V5.33333H7.76471V4.66667H8.41177V4H9.05882V3.33333H9.70588V2.66667H10.3529V0.666667H9.05882V1.33333H8.41177V2H7.76471V2.66667H7.11765V3.33333H6.47059V4H5.82353V4.66667H5.17647V5.33333H4.52941H3.88235V4.66667H3.23529V4H2.58824V3.33333H1.94118V2.66667H0.647059Z"
                        fill="#7DFE03"
                      />
                    </svg>
                    <span
                      className="text-[#7dfe03] text-xs"
                      style={{ textShadow: generateTextShadow(0.5) }}
                    >
                      {t("streakMissions.claimed")}
                    </span>
                  </div>
                )}
              </>
            )}
            {canClaim && !isPending && <PrizeSparkles />}

            {/* Pending spinner overlay */}
            {isPending && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                <Spinner className="w-5 h-5" />
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
